import {
  ClientFormData,
  ClientFilters,
  ClientInteraction,
} from '../types/client-types';
import { privateApi } from '@/config';

// Mapper para transformar ClientFormData al formato esperado por el backend
function mapClientFormDataToBackendFormat(clientData: ClientFormData): any {
  // Mapeo de tipos de cliente (ya están en español en el frontend, pero mantener compatibilidad)
  const typeMapping: Record<string, string> = {
    'buyer': 'comprador',
    'seller': 'vendedor',
    'investor': 'ambos', // Si no hay equivalente directo, usar 'ambos'
    'tenant': 'inquilino',
    'landlord': 'propietario',
    // Valores ya en español (para compatibilidad)
    'comprador': 'comprador',
    'vendedor': 'vendedor',
    'ambos': 'ambos',
    'inquilino': 'inquilino',
    'propietario': 'propietario',
  };

  // Mapeo de estados del cliente (ya están en español en el frontend, pero mantener compatibilidad)
  const statusMapping: Record<string, string> = {
    'active': 'activo',
    'prospect': 'prospecto',
    'inactive': 'inactivo',
    'closed': 'perdido',
    // Valores ya en español (para compatibilidad)
    'activo': 'activo',
    'prospecto': 'prospecto',
    'inactivo': 'inactivo',
    'perdido': 'perdido',
  };

  // Construir el nombre completo desde firstName y lastName
  const name = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim();

  // Validar y formatear fecha de nacimiento
  // Si dateOfBirth está vacío o es solo espacios, no incluirlo
  let birthDate: string | undefined = undefined;
  if (clientData.dateOfBirth && typeof clientData.dateOfBirth === 'string' && clientData.dateOfBirth.trim()) {
    const dateStr = clientData.dateOfBirth.trim();
    // El campo de fecha HTML devuelve formato YYYY-MM-DD que es válido ISO 8601
    // Validar que sea una fecha válida usando una expresión regular
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (isoDateRegex.test(dateStr)) {
      // Verificar que sea una fecha válida
      const date = new Date(dateStr + 'T00:00:00.000Z'); // Agregar hora para evitar problemas de zona horaria
      if (!isNaN(date.getTime()) && date.toISOString().startsWith(dateStr)) {
        // Formatear como ISO 8601 completo para mayor compatibilidad
        // El validador IsDateString() de class-validator acepta YYYY-MM-DD y formatos completos
        birthDate = dateStr; // YYYY-MM-DD es válido ISO 8601
      }
    }
  }

  // Validar email - debe ser una cadena no vacía y válida
  const email = clientData.email?.trim();
  if (!email) {
    throw new Error('El email es requerido');
  }

  // Preparar el objeto de retorno
  const payload: any = {
    // Información básica
    name: name,
    email: email,
    phone: clientData.phone || undefined,
    
    // Tipo y estado (usar el mapping si es necesario, sino usar directamente)
    type: typeMapping[clientData.clientType] || clientData.clientType || 'comprador',
    status: statusMapping[clientData.clientStatus] || clientData.clientStatus || 'prospecto',
    assignedAgentId: clientData.assignedAgent || undefined,
    
    // Información de contacto
    address: clientData.address || undefined,
    city: clientData.city || undefined,
    state: clientData.state || undefined,
    zipCode: clientData.zipCode || undefined,
    country: clientData.country || undefined,
    
    // Información personal
    documentType: clientData.idType || undefined,
    documentNumber: clientData.idNumber || undefined,
    
    // Información financiera
    budget: clientData.budget || undefined,
    budgetCurrency: clientData.currency || 'USD',
    
    // Fuente y referencias
    source: clientData.source || undefined,
  };

  // Solo agregar birthDate si tiene un valor válido
  if (birthDate) {
    payload.birthDate = birthDate;
  }

  // Agregar metadatos
  payload.metadata = {
    preferredContactMethod: clientData.preferredContactMethod,
    preferredContactTime: clientData.preferredContactTime,
    consentMarketing: clientData.consentMarketing,
    consentDataProcessing: clientData.consentDataProcessing,
    consentThirdParty: clientData.consentThirdParty,
    monthlyIncome: clientData.monthlyIncome,
    employmentStatus: clientData.employmentStatus,
    employer: clientData.employer,
    jobTitle: clientData.jobTitle,
    secondaryPhone: clientData.secondaryPhone,
    nationality: clientData.nationality,
    propertyTypes: clientData.propertyTypes,
    minPrice: clientData.minPrice,
    maxPrice: clientData.maxPrice,
    minBedrooms: clientData.minBedrooms,
    maxBedrooms: clientData.maxBedrooms,
    minBathrooms: clientData.minBathrooms,
    maxBathrooms: clientData.maxBathrooms,
    minArea: clientData.minArea,
    maxArea: clientData.maxArea,
    preferredLocation: clientData.preferredLocation,
    funnelStage: clientData.funnelStage,
  };

  // Notas
  payload.notes = clientData.notes || undefined;

  return payload;
}

// API para clientes
// NOTA: El backend usa la ruta '/agente/clients' (español), no '/agency/clients'
export const clientApi = {
  // Crear cliente
  create: async (clientData: ClientFormData) => {
    const mappedData = mapClientFormDataToBackendFormat(clientData);
    const response = await privateApi.post('/agency/clients', mappedData);
    return response.data;
  },

  // Actualizar cliente (usa CRM leads, misma fuente que el listing)
  update: async (id: string, clientData: Partial<ClientFormData>) => {
    // Mapear a formato CRM lead (firstName/lastName separados)
    const payload: any = {
      firstName: clientData.firstName || '',
      lastName: clientData.lastName || '',
      email: clientData.email,
      phone: clientData.phone || undefined,
      source: clientData.source || undefined,
      notes: clientData.notes || undefined,
    };
    const response = await privateApi.put(`/crm/leads/${id}`, payload);
    return response.data?.data || response.data;
  },

  // Obtener clientes de la agencia
  getAgencyClients: async (filters?: ClientFilters, endpoint?: string) => {
    const url = endpoint || '/agency/clients';
    const response = await privateApi.get(url, { params: filters });
    return response.data;
  },

  // Obtener clientes asignados al agente actual
  // TODO: Este endpoint no existe en el backend, usar GET /agente/clients con filtros
  getMyClients: async (filters?: ClientFilters) => {
    // Por ahora, usar el endpoint general que filtra por usuario actual
    const response = await privateApi.get('/agente/clients', { params: filters });
    return response.data;
  },

  // Obtener cliente por ID (usa CRM leads, misma fuente que el listing)
  getById: async (id: string) => {
    const response = await privateApi.get(`/crm/leads/${id}`);
    return response.data?.data || response.data;
  },

  // Eliminar cliente (usa CRM leads, misma fuente que el listing)
  delete: async (id: string) => {
    await privateApi.delete(`/crm/leads/${id}`);
  },

  // Obtener interacciones de un cliente
  // TODO: Este endpoint no está implementado en el backend
  getInteractions: async (clientId: string) => {
    throw new Error('Endpoint no implementado: GET /agente/clients/:id/interactions');
    // const response = await privateApi.get(`/agente/clients/${clientId}/interactions`);
    // return response.data;
  },

  // Agregar interacción
  // TODO: Este endpoint no está implementado en el backend
  addInteraction: async (
    clientId: string,
    interaction: Omit<ClientInteraction, 'id' | 'clientId' | 'date'>
  ) => {
    throw new Error('Endpoint no implementado: POST /agente/clients/:id/interactions');
    // const response = await privateApi.post(
    //   `/agente/clients/${clientId}/interactions`,
    //   interaction
    // );
    // return response.data;
  },

  // Actualizar estado del cliente
  updateStatus: async (id: string, status: string) => {
    const response = await privateApi.patch(`/agente/clients/${id}/status`, {
      status,
    });
    return response.data;
  },

  // Obtener estadísticas de clientes
  getStats: async () => {
    const response = await privateApi.get('/agente/clients/stats');
    return response.data;
  },

};

// API para ubicaciones (reutilizada de property-api.ts)
export const locationApi = {
  // Obtener países
  getCountries: async () => {
    const response = await privateApi.get('/locations/countries');
    return response.data;
  },

  // Obtener ciudades
  getCities: async (country?: string) => {
    const response = await privateApi.get('/locations/cities', {
      params: country ? { country } : {},
    });
    return response.data;
  },

  // Obtener zonas
  getZones: async (city?: string) => {
    const response = await privateApi.get('/locations/zones', {
      params: city ? { city } : {},
    });
    return response.data;
  },
};
